import React from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import Seo, { useLocalBusinessLd, SITE_URL } from "@/components/Seo";
import StatsBand from "@/components/StatsBand";
import Breadcrumbs from "@/components/Breadcrumbs";
import TrustPartner from "@/components/TrustPartner";
import { useContent } from "@/hooks/useContent";
import { CASE_STUDIES_DEFAULTS } from "@/lib/contentDefaults";
import { ArrowRight, Phone, MapPin, Check, TrendingUp } from "lucide-react";

const HERO = "https://images.pexels.com/photos/6474532/pexels-photo-6474532.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1600";

const RELATED = [
  { to: "/conciergerie-airbnb-rouen", label: "Conciergerie Airbnb Rouen", text: "La gestion complète de A à Z." },
  { to: "/tarifs-conciergerie-rouen", label: "Nos tarifs", text: "20 % de commission, ménage payé par le voyageur." },
  { to: "/estimation-revenus-airbnb-rouen", label: "Estimer mes revenus", text: "Estimation gratuite et sans engagement." },
  { to: "/reglementation-airbnb-rouen", label: "Réglementation 2026", text: "Loi Le Meur, 120 jours, DPE, sanctions." },
];

export default function ResultsOwners() {
  const businessLd = useLocalBusinessLd();
  const data = useContent("nj_case_studies", CASE_STUDIES_DEFAULTS);
  const cases = (data.items || []).filter((c) => !c.hidden);

  const metaTitle = "Résultats propriétaires à Rouen : études de cas de conciergerie | NJ Conciergerie";
  const metaDescription = "Découvrez des résultats concrets de propriétaires ayant confié leur logement à NJ Conciergerie à Rouen : occupation, revenus et avis avant/après. Études de cas transparentes.";

  return (
    <Layout>
      <Seo title={metaTitle} description={metaDescription} canonical="/resultats-proprietaires"
        image={HERO} jsonLd={[businessLd, {
          "@context": "https://schema.org", "@type": "CollectionPage",
          name: "Résultats propriétaires à Rouen", description: metaDescription,
          url: `${SITE_URL}/resultats-proprietaires`,
        }]} />

      {/* HERO */}
      <section className="relative h-[48vh] min-h-[360px] flex items-center" data-testid="results-owners">
        <img src={HERO} alt="Résultats propriétaires à Rouen" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-nj-dark/60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <span className="text-nj-gold text-xs tracking-[0.28em] uppercase font-semibold">Résultats propriétaires · Rouen</span>
          <h1 className="font-serif font-light text-white text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-3xl leading-[1.08]">Des résultats concrets pour les propriétaires rouennais</h1>
          <p className="text-white/85 text-lg mt-5 max-w-2xl leading-relaxed">{data.intro}</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/estimation-revenus-airbnb-rouen" data-testid="results-cta-primary" className="bg-nj-gold text-nj-dark font-semibold px-7 py-3.5 rounded-sm hover:brightness-105 transition-all inline-flex items-center gap-2">
              Estimer mes revenus <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="tel:+33666934267" className="border border-white/60 text-white font-semibold px-7 py-3.5 rounded-sm hover:bg-white/10 transition-colors inline-flex items-center gap-2">
              <Phone className="w-4 h-4" /> 06 66 93 42 67
            </a>
          </div>
        </div>
      </section>

      <Breadcrumbs items={[
        { label: "Accueil", to: "/" },
        { label: "Conciergerie à Rouen", to: "/conciergerie-airbnb-rouen" },
        { label: "Résultats propriétaires" },
      ]} />

      <StatsBand />

      {/* CASE STUDIES */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {cases.length === 0 && (
          <p className="text-center text-nj-muted" data-testid="results-empty">Nos études de cas seront publiées prochainement. Contactez-nous pour discuter de votre projet.</p>
        )}
        <div className="space-y-14">
          {cases.map((c, idx) => (
            <article key={c.id || idx} data-testid={`case-study-${idx}`} className="bg-white border border-nj-olive rounded-lg overflow-hidden shadow-[0_1px_0_rgba(28,63,58,0.04)]">
              <div className="border-b border-nj-olive px-6 sm:px-9 py-6">
                <h2 className="font-serif font-light text-2xl sm:text-3xl text-nj-text">{c.title}</h2>
                <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-nj-muted">
                  {c.type && <span>{c.type}</span>}
                  {c.quartier && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {c.quartier}</span>}
                </div>
              </div>
              <div className="px-6 sm:px-9 py-7 grid lg:grid-cols-2 gap-8">
                <div>
                  {c.situation && (
                    <>
                      <h3 className="text-nj-gold text-[11px] tracking-[0.28em] uppercase font-semibold mb-2">La situation</h3>
                      <p className="text-nj-text2 leading-relaxed mb-6">{c.situation}</p>
                    </>
                  )}
                  {(c.actions || []).length > 0 && (
                    <>
                      <h3 className="text-nj-gold text-[11px] tracking-[0.28em] uppercase font-semibold mb-3">Nos actions</h3>
                      <ul className="space-y-2.5">
                        {c.actions.map((a, i) => (
                          <li key={i} className="flex gap-2.5 text-nj-text2 leading-relaxed"><Check className="w-4 h-4 text-nj-primary mt-1 shrink-0" /><span>{a}</span></li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
                <div>
                  {(c.results || []).length > 0 && (
                    <>
                      <h3 className="text-nj-gold text-[11px] tracking-[0.28em] uppercase font-semibold mb-3 inline-flex items-center gap-1.5"><TrendingUp className="w-4 h-4" /> Les résultats</h3>
                      <div className="space-y-3">
                        {c.results.map((r, i) => (
                          <div key={i} className="bg-nj-olive/40 rounded-md px-4 py-3">
                            <div className="text-sm text-nj-muted">{r.k}</div>
                            <div className="flex items-center gap-3 mt-1">
                              {r.before && <span className="text-nj-muted line-through text-sm">{r.before}</span>}
                              {r.before && <ArrowRight className="w-4 h-4 text-nj-primary" />}
                              <span className="font-serif text-2xl text-nj-primary">{r.after}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {c.quote && (
                    <blockquote className="mt-6 border-l-2 border-nj-gold pl-4 italic text-nj-text2 leading-relaxed">{c.quote}</blockquote>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <TrustPartner />

      {/* RELATED */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h2 className="font-serif text-2xl text-nj-text mb-6">À explorer aussi</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {RELATED.map((r) => (
            <a key={r.to} href={r.to} className="block bg-white border border-nj-olive rounded-md p-5 hover:border-nj-primary transition-colors">
              <span className="font-medium text-nj-text">{r.label}</span>
              <p className="text-sm text-nj-muted mt-1">{r.text}</p>
            </a>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-nj-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="font-serif font-light text-3xl sm:text-4xl">Et si c'était votre logement ?</h2>
          <p className="text-white/75 mt-3">Obtenez une estimation gratuite et sans engagement de vos revenus à Rouen.</p>
          <div className="flex flex-wrap gap-3 justify-center mt-7">
            <Link to="/estimation-revenus-airbnb-rouen" className="bg-nj-gold text-nj-dark font-semibold px-7 py-3.5 rounded-sm hover:brightness-105 transition-all inline-flex items-center gap-2">
              Estimer mes revenus <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/confier-mon-logement" className="border border-white/60 text-white font-semibold px-7 py-3.5 rounded-sm hover:bg-white/10 transition-colors inline-flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Confier mon logement
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
